<template>
  <b-modal
    id="edit-requirement-modal"
    name="edit-requirement-modal"
    ref="editreqmodal"
    title="Edit requirement"
    size="lg"
    :busy="submitting"
    ok-title="Update"
    :ok-disabled="!name || loading"
    close-title="Cancel"
    hide-header-close
    @shown="shown"
    @ok="handleOk">
    <form id="update-requirement-form">
      <b-form-group :feedback="nameInvalidFeedback"
                    :state="nameState">
        <b-form-input type="text"
                      placeholder="Requirement name"
                      v-model="name"
                      data-vv-name="name"
                      v-validate="'required'">
        </b-form-input>
        <!--<icon v-show="errors.has('name')" name="warning"></icon>-->
        <!--<span v-show="errors.has('name')" class="help is-danger">{{ errors.first('name') }}</span>-->
      </b-form-group>
      <b-form-group description="Set a custom ID for this Requirement">
        <b-form-input v-model="displayID"
                      type="text"
                      placeholder="Requirement ID (optional)">
        </b-form-input>
      </b-form-group>
      <b-form-group description="These types can be managed on the rmCloud Configuration screen"
                    :feedback="requirementTypeInvalidFeedback"
                    :state="requirementTypeState">
        <span id="requirement-type-select">
        <multiselect v-model="selectedRequirementType"
                     :disabled="selectedRequirements.length > 0"
                     :options="requirementTypes"
                     :loading="requirementTypesLoading"
                     :allow-empty="false"
                     :close-on-select="true"
                     :hide-selected="false"
                     placeholder="Select a requirement type"
                     label="name"
                     track-by="requirement_type_id">
        </multiselect>
        </span>
        <b-popover target="requirement-type-select"
                   triggers="hover"
                   content="To edit the requirement type, remove any linked requirements below."
                   :disabled="selectedRequirements.length === 0">
        </b-popover>
      </b-form-group>
      <b-form-group>
        <b-form-textarea v-model="description"
                         placeholder="Description"
                         :rows="2"
                         :max-rows="6">
        </b-form-textarea>
      </b-form-group>
      <b-form-group v-if="!currentProjectIsGlobal">
        <multiselect v-model="selectedStatus"
                     :options="statuses"
                     :loading="statusesLoading"
                     :close-on-select="true"
                     :hide-selected="false"
                     placeholder="Status"
                     label="name"
                     track-by="id">
        </multiselect>
      </b-form-group>
      <b-form-group>
        <multiselect v-model="selectedRequirements"
                     :options="availableRequirements"
                     :multiple="true"
                     :loading="requirementsLoading"
                     :custom-label="customRequirementLabel"
                     :close-on-select="true"
                     :clear-on-select="false"
                     :hide-selected="true"
                     :placeholder="getRequirementsPlaceholder()"
                     track-by="requirement_id">
        </multiselect>
      </b-form-group>
      <b-form-group id="issues-multiselect-group-edit">
        <multiselect v-model="selectedIssues"
                     :options="sortedIssues"
                     :multiple="true"
                     :loading="issuesLoading"
                     :custom-label="customIssueLabel"
                     :close-on-select="true"
                     :clear-on-select="false"
                     :hide-selected="true"
                     :disabled="shouldDisableIssuesMultiselect"
                     placeholder="Select issue(s)"
                     track-by="id">
        </multiselect>
      </b-form-group>
      <b-tooltip v-if="shouldDisableIssuesMultiselect" target="issues-multiselect-group-edit" triggers="hover">
        No issues found on this Jira project!
      </b-tooltip>
      <b-form-group v-if="$auth.user().installed_on_confluence">
        <multiselect v-model="selectedContents"
                     :options="contents"
                     :loading="contentsLoading"
                     :multiple="true"
                     :close-on-select="true"
                     :clear-on-select="false"
                     :hide-selected="true"
                     :max="20"
                     placeholder="Select Confluence content"
                     label="title"
                     track-by="id">
        </multiselect>
      </b-form-group>
      <b-form-group v-if="!currentProjectIsGlobal">
        <multiselect v-model="selectedComponents"
                     :options="components"
                     :multiple="true"
                     :loading="componentsLoading"
                     :close-on-select="true"
                     :clear-on-select="false"
                     :hide-selected="true"
                     placeholder="Component(s)"
                     label="name"
                     track-by="id">
        </multiselect>
      </b-form-group>
      <b-form-group v-if="!currentProjectIsGlobal">
        <multiselect v-model="selectedAssignee"
                     :options="assignees"
                     :loading="assigneesLoading"
                     :close-on-select="true"
                     :hide-selected="false"
                     placeholder="Assign to"
                     label="displayName"
                     track-by="key">
        </multiselect>
      </b-form-group>
      <b-form-group v-for="attribute in attributes" :key="attribute.attribute_id">
        <div v-if="attribute.type === 'select'">
          <label>{{attribute.name}}</label>
          <b-form-select v-model="requirement.attributes[attribute.attribute_id]"
                         :options="getOptionsArray(attribute.options)">

          </b-form-select>
        </div>
        <b-form-input v-else type="text"
                      :placeholder="attribute.name"
                      v-model="requirement.attributes[attribute.attribute_id]">
        </b-form-input>
      </b-form-group>
    </form>
    <div slot="modal-ok" v-if="submitting">
      <icon name="spinner" pulse></icon> Updating
    </div>
  </b-modal>
</template>

<script>
  import dialogMixin from '@/mixins/dialog'
  import requirementModalMixin from '@/mixins/requirementModal'
  import utilsMixin from '@/mixins/utils'
  export default {
    computed: {
      currentProject () {
        return this.$store.state.currentProject
      }
    },
    data () {
      return {}
    },
    props: {
      id: Number
    },
    mixins: [dialogMixin, requirementModalMixin, utilsMixin],
    mounted () {
      // Ideally I'd get rid of this, but it's needed for the first time the modal is opened
      this.getStatusesForCurrentProject()
      this.getRequirementAndSelectedDataFromRmcloud()
    },
    methods: {
      handleOk (e) {
        e.preventDefault()
        // This validateAll is needed for the case when nothing has been entered yet
        // Otherwise it was letting it validate
        // The empty then/catch avoids a console error
        this.$validator.validateAll().then(() => {
        }).catch(() => {
        })
        if (this.errors.any()) {
          return e.cancel()
        } else {
          this.submitting = true
          this.submitData()
        }
      },
      shown () {
        this.getStatusesForCurrentProject()
        this.getRequirementAndSelectedDataFromRmcloud()
      },
      getRequirementAndSelectedDataFromRmcloud () {
        this.$http.get(
          '/api/requirement/' + this.id
        ).then(response => {
          this.attachments = response.data.requirement.attachments
          this.requirement = response.data.requirement
          this.name = response.data.requirement.name
          this.displayID = response.data.requirement.display_id
          this.description = response.data.requirement.description

          this.getRequirements()
          this.getAttributes()
          this.getRequirementTypes()
          if (!this.currentProjectIsGlobal) {
            this.getAssignees()
            this.getComponents(response.data.requirement.components)
          }

          // Type
          this.selectedRequirementType = this.requirementTypes.find(r => r.requirement_type_id === response.data.requirement.requirement_type.requirement_type_id)

          // Contents
          if (this.$auth.user().installed_on_confluence) {
            this.getContents(response.data.requirement.contents)
          }

          // Issues
          this.selectedIssueIds = response.data.requirement.issues.map(d => d.atlassian_issue_id)
          this.getIssues()

          // Status
          if (this.requirement.status) {
            this.selectedStatus = this.statuses.find(s => s.id === this.requirement.status.atlassian_status_id.toString())
          } else {
            this.selectedStatus = null
          }
        }, response => {
        })
      },
      getAssignees () {
        this.assigneesLoading = true
        window.AP.request({
          url: `/rest/api/3/user/assignable/search?maxResults=1000&project=${this.currentProject.key}`,
          success: response => {
            // convert the string response to JSON
            response = JSON.parse(response)
            this.assignees = response
            if (this.requirement.assigned_to) {
              this.selectedAssignee = this.assignees.find(s => s.accountId === this.requirement.assigned_to.account_id)
            } else {
              this.selectedAssignee = null
            }
            this.assigneesLoading = false
          },
          error: () => {
            window.AP.flag.create({
              title: 'Error',
              body: 'There was a problem loading assignee data from Atlassian.  Please contact rmCloud support via the "Get help" button',
              type: 'error'
            })
          }
        })
      },
      getComponents (selectedComponents) {
        this.componentsLoading = true
        window.AP.request({
          url: `/rest/api/3/project/${this.currentProject.id}/components`,
          success: response => {
            // convert the string response to JSON
            response = JSON.parse(response)
            this.components = response
            const selectedComponentIds = selectedComponents.map(c => c.atlassian_component_id)
            this.selectedComponents = this.components.filter(c => selectedComponentIds.includes(Number.parseInt(c.id)))
            this.componentsLoading = false
          },
          error: () => {
            window.AP.flag.create({
              title: 'Error',
              body: 'There was a problem loading component data from Atlassian.  Please contact rmCloud support via the "Get help" button',
              type: 'error'
            })
          }
        })
      },
      getContents (selectedContents) {
        this.contentsLoading = true
        this.$http.get(
          'api/confluence/content'
        ).then(response => {
          this.contents = response.data.results
          const selectedContentIds = selectedContents.map(c => c.atlassian_content_id)
          this.selectedContents = this.contents.filter(c => selectedContentIds.includes(Number.parseInt(c.id)))
          this.contentsLoading = false
        }, response => {
          window.AP.flag.create({
            title: 'Error',
            body: 'There was a problem loading confluence data from Atlassian.  Please contact rmCloud support via the "Get help" button',
            type: 'error'
          })
        })
      },
      getRequirements () {
        this.requirementsLoading = true
        this.$http.get(
          '/api/requirement?atlassian_project_id=' + this.currentProject.id
        ).then(response => {
          this.requirements = response.data.requirements.filter(r => r.requirement_id !== this.requirement.requirement_id)

          // Need to get the selected requirements, which we already have as an arary of full requirement dicts
          // First get an array of requirement ids e.g. [1,2,3,4,5]
          // Next filter the full requirements list by requirements that are in that array
          const selectedRequirementIds = this.requirement.linked_requirements_v2.map(r => r.requirement_id)
          this.selectedRequirements = this.requirements.filter(r => selectedRequirementIds.includes(r.requirement_id))
          this.requirementsLoading = false
        }, response => {
          window.AP.flag.create({
            title: 'Error',
            body: 'There was a problem loading requirement data from rmCloud.  Please contact rmCloud support via the "Get help" button',
            type: 'error'
          })
        })
      },
      getRequirementTypes () {
        this.requirementTypesLoading = true
        this.$http.get(
          '/api/requirement/type'
        ).then(response => {
          this.requirementTypes = response.data.requirement_types
          this.selectedRequirementType = this.requirementTypes.find(r => r.requirement_type_id === this.requirement.requirement_type.requirement_type_id)
          this.requirementTypesLoading = false
        }, response => {
          this.requirementTypesLoading = false
          window.AP.flag.create({
            title: 'Error',
            body: 'There was a problem loading requirement type data from rmCloud.  Please contact rmCloud support via the "Get help" button',
            type: 'error'
          })
        })
      },
      setTheState () {
        // This potentially updates the name, so both the list and the detail need updating
        this.$store.commit('setNeedToRefreshRequirementsDetail', true)
        this.$store.commit('setNeedToRefreshItemsList', true)
      },
      submitData () {
        this.$ga.event('requirement', 'edit', this.name, 1)
        this.$http.put(
          '/api/requirement/' + this.id,
          {
            assigned_to: this.selectedAssignee,
            attributes: this.requirement.attributes,
            attachments: this.attachments,
            name: this.name,
            components: this.selectedComponents,
            contents: this.selectedContents,
            description: this.description,
            display_id: this.displayID,
            issues: this.selectedIssues,
            linked_requirements: this.selectedRequirements,
            requirement_type_id: this.selectedRequirementType.requirement_type_id,
            status: this.selectedStatus
          }
        ).then(response => {
          window.AP.flag.create({
            title: 'Success!',
            body: 'Requirement edited successfully',
            type: 'success',
            close: 'auto'
          })
          this.submitting = false
          this.setTheState()
          this.$refs.editreqmodal.hide()
        }, error => {
          let errorString = this.createErrorStringFromResponse(error.response.data)
          window.AP.flag.create({
            title: 'Error',
            body: `There was a problem editing your requirement: ${errorString}`,
            type: 'error',
            close: 'auto'
          })
          this.submitting = false
        })
      }
    }
  }
</script>

<style>
  .multiselect__tag {
    max-width: 100%;
    overflow-x: hidden;
    text-overflow: ellipsis;
  }
</style>



// WEBPACK FOOTER //
// EditRequirementModal.vue?2bff3c6a